import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CategoryWithMonthData } from '../types/category';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  category?: CategoryWithMonthData | null;
  title: string;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  category,
  title,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(category?.name || '');
      setError('');
    }
  }, [isOpen, category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (name.trim().length < 2) {
      setError(t('category_modal.group_name_error'));
      return;
    }

    setLoading(true);
    try {
      await onSave(name.trim());
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('category_modal.save_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {t('category_modal.group_name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder={t('category_modal.group_name_placeholder')}
              autoFocus
            />
          </div>

          {error && (
            <div
              className="bg-red-500 border-2 border-red-700 px-3 py-2"
              style={{ boxShadow: '3px 3px 0 #c0392b' }}
            >
              <p className="font-mono text-xs text-white">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '4px 4px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {t('category_modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase disabled:opacity-50"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {loading ? t('category_modal.saving') : t('category_modal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
